import { getDescendants, NodeModel } from '@minoru/react-dnd-treeview'
import { action, computed, makeObservable, observable } from 'mobx'

import {
  IConditionalField,
  IInstructions,
  IPermitTypeField,
  IWorkflowStep,
  PermitFieldType,
  WorkflowStepType,
} from '~/client/graph/types.generated'
import formFieldsCaptionMap from '~/client/src/desktop/constants/formFieldsCaptionMap'
import IFormFilterFieldOption from '~/client/src/desktop/interfaces/IFormFilterFieldOption'
import {
  expandablePermitFieldTypes,
  getDefaultAllowCreateNew,
  getDefaultFilterOptions,
  getNonRemovableFieldTypes,
  locationPermitFieldTypes,
  nestablePermitFields,
  nonHiddeablePermitFieldTypes,
  singleInstancesPermitFields,
  workflowFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import {
  MAX_CONDITIONAL_DEPTH_LEVEL,
  MAX_FIELDS_DEPTH,
  MAX_FIELDS_NUMBER,
} from '~/client/src/shared/constants/permitTypeFieldsConstants'
import QuestionnaireType from '~/client/src/shared/enums/QuestionnaireType'
import { InstructionListType } from '~/client/src/shared/models/WorkflowType'
import BsonObjectId from '~/client/src/shared/utils/BsonObjectId'
import {
  areArraysEqual,
  areObjectArraysEqual,
  copyArray,
  copyObjectDeep,
} from '~/client/src/shared/utils/util'

export const TREE_ROOT_ID = 'tree-root-id'

function sortTreeData(
  treeData: NodeModel<IPermitTypeField>[],
  sortedData: NodeModel<IPermitTypeField>[],
  parentId: string,
) {
  const children = treeData.filter(node => node.parent === parentId)

  children.forEach(node => {
    sortedData.push(node)
    sortTreeData(treeData, sortedData, node.id.toString())
  })
}

function getDepth(
  tree: NodeModel<IPermitTypeField>[],
  id: string,
  depth = 0,
): number {
  if (depth > MAX_FIELDS_DEPTH) {
    return depth
  }

  const target = tree.find(node => node.id === id)

  if (target) {
    return getDepth(tree, target.parent?.toString(), depth + 1)
  }

  return depth
}

function getChildrenDepth(
  tree: NodeModel<IPermitTypeField>[],
  ids: string[],
  depth = 0,
): number {
  if (depth > MAX_FIELDS_DEPTH) {
    return depth
  }

  const nestableItems = tree.filter(
    node => ids.includes(node.parent.toString()) && node.droppable,
  )

  if (nestableItems.length) {
    return getChildrenDepth(
      tree,
      nestableItems.map(({ id }) => id.toString()),
      depth + 1,
    )
  }

  return depth
}

export default class FormConfiguratorStore {
  @observable public isRemoveDialogShown = false
  @observable public availableFilterFieldsOptions: IFormFilterFieldOption[] = []
  @observable public filterFieldsMap: Map<string, IPermitTypeField> = null

  @observable private workflowStep: IWorkflowStep = null
  @observable private isInitialFormStep: boolean = null
  @observable private workflowType: string = ''
  @observable private removeFieldId: string = null
  @observable private readonly collapsedFieldIds = new Set<string>()
  @observable public readonly expandedConfigFieldIds = new Set<string>()

  public constructor(
    workflowStep: IWorkflowStep,
    isInitialFormStep: boolean,
    workflowType: string,
    availableFilterFieldsOptions: IFormFilterFieldOption[],
    filterFieldsMap: Map<string, IPermitTypeField>,
    private readonly onChange: (workflowStep: IWorkflowStep) => void,
    private onRemoveFieldFromNotifications?: (fieldId: string) => void,
  ) {
    this.setWorkflowStep(workflowStep)
    this.setIsInitialFormStep(isInitialFormStep)
    this.setWorkflowType(workflowType)
    this.setAvailableFilterFieldsOptions(availableFilterFieldsOptions)
    this.setFilterFieldsMap(filterFieldsMap)
    makeObservable(this)
  }

  @action.bound
  public setWorkflowStep(workflowStep: IWorkflowStep) {
    this.workflowStep = {
      ...workflowStep,
      fields: workflowStep.fields.map(f => copyObjectDeep(f)),
      conditionalFields: workflowStep.conditionalFields.map(cf =>
        copyObjectDeep(cf),
      ),
      workflowRuleIds: copyArray(workflowStep.workflowRuleIds || []),
      notificationFieldIds: copyArray(workflowStep.notificationFieldIds || []),
    }

    const currentFieldIds = new Set(workflowStep.fields.map(f => f.id))
    this.expandedConfigFieldIds.forEach(id => {
      if (!currentFieldIds.has(id)) {
        this.expandedConfigFieldIds.delete(id)
      }
    })
  }

  @action.bound
  public setIsInitialFormStep(isInitialFormStep: boolean) {
    this.isInitialFormStep = isInitialFormStep
  }

  @action.bound
  public setWorkflowType(workflowType: string) {
    this.workflowType = workflowType
  }

  @action.bound
  public setAvailableFilterFieldsOptions(availableFilterFieldsOptions: any[]) {
    this.availableFilterFieldsOptions = availableFilterFieldsOptions
  }

  @action.bound
  public setFilterFieldsMap(filterFieldsMap: Map<string, IPermitTypeField>) {
    this.filterFieldsMap = filterFieldsMap
  }

  public get isRecurringInspection(): boolean {
    return this.workflowStep.type === WorkflowStepType.RecurringInspection
  }

  public get fields(): IPermitTypeField[] {
    return this.workflowStep.fields
  }

  public get conditionalFields(): IConditionalField[] {
    return this.workflowStep.conditionalFields
  }

  public get treeData(): NodeModel<IPermitTypeField>[] {
    return this.fields.map(field => ({
      id: field.id,
      parent: field.parentId || TREE_ROOT_ID,
      text: field.id,
      droppable: nestablePermitFields.includes(field.type),
      data: field,
    }))
  }

  public get openedFieldIds(): string[] {
    return this.fields
      .filter(
        ({ id, type }) =>
          nestablePermitFields.includes(type) &&
          !this.collapsedFieldIds.has(id),
      )
      .map(({ id }) => id)
  }

  public get isAddBtnHidden(): boolean {
    return this.fields.length >= MAX_FIELDS_NUMBER
  }

  public get singleUseFields(): PermitFieldType[] {
    return singleInstancesPermitFields.filter(pf =>
      this.fields.some(f => f.type === pf),
    )
  }

  public get restrictedFields(): PermitFieldType[] {
    return this.isInitialFormStep ? [] : workflowFieldTypes
  }

  public canDropField = (
    tree: NodeModel<IPermitTypeField>[],
    dragSource: NodeModel<IPermitTypeField>,
    dropTargetId: string,
    dropTarget: NodeModel<IPermitTypeField>,
  ): boolean => {
    if (dragSource?.parent === dropTargetId) {
      return true
    }

    if (dropTarget && dragSource?.droppable) {
      const depth = getDepth(tree, dropTarget.id.toString())

      if (depth > MAX_FIELDS_DEPTH) {
        return false
      }
      const childrenDepth = getChildrenDepth(tree, [dragSource.id.toString()])
      if (childrenDepth + depth > MAX_FIELDS_DEPTH) {
        return false
      }
    }
  }

  public canRemoveField = (field: IPermitTypeField): boolean => {
    const nonRemovable = getNonRemovableFieldTypes(this.workflowType)
    if (nonRemovable.length === 0) {
      return true
    }

    if (field.isUsedInMaterial) {
      return false
    }

    if (nonRemovable.includes(field.type)) {
      return false
    }
    return getDescendants(this.treeData, field.id).every(
      f => !nonRemovable.includes(f.data.type),
    )
  }

  public getRestrictedFieldsByDepth = (
    currentFieldDepth: number,
  ): PermitFieldType[] => {
    return this.restrictedFields.concat(
      currentFieldDepth >= MAX_FIELDS_DEPTH ? nestablePermitFields : [],
    )
  }

  @action.bound
  public showRemoveDialog(fieldId: string) {
    this.isRemoveDialogShown = true

    this.removeFieldId = fieldId
  }

  @computed
  public get allFieldSettingsCollapsed(): boolean {
    return this.expandedConfigFieldIds.size === 0
  }

  @action.bound
  public toggleAllFieldSettings() {
    if (this.expandedConfigFieldIds.size > 0) {
      this.expandedConfigFieldIds.clear()
    } else {
      this.fields
        .filter(f => expandablePermitFieldTypes.includes(f.type))
        .forEach(f => this.expandedConfigFieldIds.add(f.id))
    }
  }

  @action.bound
  public toggleFieldConfigExpanded(fieldId: string) {
    if (this.expandedConfigFieldIds.has(fieldId)) {
      this.expandedConfigFieldIds.delete(fieldId)
    } else {
      this.expandedConfigFieldIds.add(fieldId)
    }
  }

  @action.bound
  public hideRemoveDialog() {
    this.isRemoveDialogShown = false

    this.removeFieldId = null
  }

  @action.bound
  public collapseField(fieldId: string, isCollapsed: boolean) {
    if (isCollapsed) {
      this.collapsedFieldIds.delete(fieldId)
    } else {
      this.collapsedFieldIds.add(fieldId)
    }
  }

  @action.bound
  public onChangeFields(newTreeData: NodeModel<IPermitTypeField>[]) {
    const sortedData: NodeModel<IPermitTypeField>[] = []
    sortTreeData(newTreeData, sortedData, TREE_ROOT_ID)

    if (areObjectArraysEqual(this.treeData, sortedData)) {
      return
    }

    this.workflowStep.fields = sortedData.map(({ parent, data }) => {
      data.parentId = parent === TREE_ROOT_ID ? null : parent.toString()
      return data
    })

    this.saveWorkflowStep()
  }

  @action.bound
  public addNewField(type: PermitFieldType, field?: IPermitTypeField) {
    const newField = this.getNewFieldModel(type, field)

    const index = this.fields.findIndex(({ id }) => field?.id === id)
    this.fields.splice(index + 1, 0, newField)

    if (this.collapsedFieldIds.has(field?.id)) {
      this.collapsedFieldIds.delete(field.id)
    }

    if (expandablePermitFieldTypes.includes(type)) {
      this.expandedConfigFieldIds.add(newField.id)
    }
    this.saveWorkflowStep()
  }

  @action.bound
  public updateField(field: IPermitTypeField) {
    const fieldIndex = this.fields.findIndex(({ id }) => field.id === id)

    if (fieldIndex !== -1) {
      this.fields.splice(fieldIndex, 1, field)

      this.saveWorkflowStep()
    }
  }

  @action.bound
  public removeField() {
    if (!this.removeFieldId) {
      return
    }

    const deleteIds = [
      this.removeFieldId,
      ...getDescendants(this.treeData, this.removeFieldId).map(
        node => node.id as string,
      ),
    ]
    const conditionalIds = this.getConditionalDescendants(deleteIds).map(
      cf => cf.fieldId,
    )

    this.workflowStep.fields = this.fields.filter(
      ({ id }) => !deleteIds.includes(id),
    )
    this.workflowStep.conditionalFields = this.conditionalFields.filter(
      ({ fieldId }) => !conditionalIds.includes(fieldId),
    )
    this.workflowStep.notificationFieldIds = (
      this.workflowStep.notificationFieldIds || []
    ).filter(id => !deleteIds.includes(id))

    deleteIds.forEach(id => {
      this.expandedConfigFieldIds.delete(id)
      this.onRemoveFieldFromNotifications?.(id)
    })

    this.hideRemoveDialog()

    this.saveWorkflowStep()
  }

  @action.bound
  public addNewConditionalField(
    fieldId: string,
    key: string,
    index: number,
    fieldType?: PermitFieldType,
    customFieldName?: string,
    isLBSToggleEnabled?: boolean,
  ) {
    if (!fieldId || !key?.trim()) {
      return
    }

    const newField = fieldType
      ? this.getNewFieldModel(
          fieldType,
          null,
          customFieldName,
          isLBSToggleEnabled,
        )
      : null

    const conditionalFieldObj = this.conditionalFields
      .filter(cf => cf.fieldId === fieldId)
      .find(cf => cf.key === key)

    if (conditionalFieldObj && !newField) return

    if (conditionalFieldObj) {
      conditionalFieldObj.values.splice(index + 1, 0, newField)
    } else {
      this.conditionalFields.push({
        fieldId,
        key,
        values: newField ? [newField] : [],
      })
    }

    this.saveWorkflowStep()
  }

  @action.bound
  public updateConditionalField(
    fields: IPermitTypeField[],
    index: number,
    field: IPermitTypeField,
  ) {
    fields.splice(index, 1, field)

    this.saveWorkflowStep()
  }

  @action.bound
  public reorderConditionalField(
    fields: IPermitTypeField[],
    fromIndex: number,
    toIndex: number,
  ) {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= fields.length ||
      toIndex >= fields.length
    ) {
      return
    }

    const [item] = fields.splice(fromIndex, 1)
    fields.splice(toIndex, 0, item)

    this.saveWorkflowStep()
  }

  @action.bound
  public removeConditionalField(
    fieldId: string,
    key: string,
    conditionalFieldId: string,
  ) {
    const conditionalFieldObj = this.conditionalFields
      .filter(cf => cf.fieldId === fieldId)
      .find(cf => cf.key === key)

    if (!conditionalFieldObj) {
      return
    }

    const descendantIds = !conditionalFieldId
      ? conditionalFieldObj.values.map(f => f.id)
      : [conditionalFieldId]

    const idsToDelete = this.getConditionalDescendants(descendantIds).map(
      ({ fieldId }) => fieldId,
    )

    if (!conditionalFieldId) {
      this.workflowStep.conditionalFields = this.conditionalFields.filter(
        cf => !(cf.fieldId === fieldId && cf.key === key),
      )
    } else {
      conditionalFieldObj.values = conditionalFieldObj.values.filter(
        f => f.id !== conditionalFieldId,
      )
    }

    if (idsToDelete.length > 0) {
      this.workflowStep.conditionalFields = this.conditionalFields.filter(
        ({ fieldId }) => !idsToDelete.includes(fieldId),
      )
    }

    this.saveWorkflowStep()
  }

  @action.bound
  public changePermitTypeInstruction(
    field: IPermitTypeField,
    instructions: IInstructions,
  ) {
    if (this.areInstructionsChanged(field.instructions, instructions)) {
      field.instructions = instructions

      this.saveWorkflowStep()
    }
  }

  private getNewFieldModel(
    type: PermitFieldType,
    field?: IPermitTypeField,
    fieldName?: string,
    isLBSToggleEnabled?: boolean,
  ): IPermitTypeField {
    const isInstructionField = type === PermitFieldType.Instructions
    const isChecklistField =
      type === PermitFieldType.Checklist || type === PermitFieldType.Question

    const caption: string =
      isInstructionField || isChecklistField
        ? ''
        : fieldName || formFieldsCaptionMap[type]

    const nonRemovable = getNonRemovableFieldTypes(this.workflowType)
    const isEditable =
      !nonHiddeablePermitFieldTypes.includes(type) &&
      !nonRemovable.includes(type)

    const newField: IPermitTypeField = {
      id: BsonObjectId.generate(),
      parentId: nestablePermitFields.includes(field?.type)
        ? field?.id
        : field?.parentId,
      type,
      caption,
      isMandatory: false,
      isShown: true,
      canEditIsMandatory: isEditable,
      canEditIsShown: isEditable,
      filterOptions: getDefaultFilterOptions(type),
      allowCreateNew: getDefaultAllowCreateNew(type),
      isLBSLogicEnabled:
        (locationPermitFieldTypes.includes(type) && isLBSToggleEnabled) ||
        false,
    }

    if (isInstructionField) {
      newField.instructions = {
        list: [formFieldsCaptionMap[type]],
        listType: InstructionListType.Bulleted,
      }
    }
    if (isChecklistField) {
      newField.checklist = {
        list: [
          {
            id: null,
            text: formFieldsCaptionMap[type],
            questionnaireType: QuestionnaireType.YesNoNA,
          },
        ],
        listType: InstructionListType.Enumerated,
      }
    }

    return newField
  }

  private areInstructionsChanged(
    existing: IInstructions,
    newInstructions: IInstructions,
  ): boolean {
    return (
      !areArraysEqual(existing?.list || [], newInstructions?.list || []) ||
      existing?.listType !== newInstructions?.listType
    )
  }

  private getConditionalDescendants(
    fieldIds: string[],
    depthLevel: number = 0,
  ): IConditionalField[] {
    if (depthLevel === MAX_CONDITIONAL_DEPTH_LEVEL) {
      return []
    }

    const descendants: IConditionalField[] = []
    const filteredConditionals = this.conditionalFields.filter(cf =>
      fieldIds.includes(cf.fieldId),
    )
    const childrenIds = filteredConditionals.flatMap(({ values }) =>
      values.map(({ id }) => id),
    )

    descendants.push(
      ...filteredConditionals,
      ...this.getConditionalDescendants(childrenIds, depthLevel + 1),
    )

    return descendants
  }

  private saveWorkflowStep = () => {
    this.onChange(this.workflowStep)
  }
}
